/* eslint-disable no-useless-escape */
// Internals
const agent =
  typeof navigator !== 'undefined'
    ? navigator.userAgent || navigator.vendor || window.opera
    : ''
const PSEUDOELEM_CHECK_CACHE = {}
const IS_BROWSER = typeof document !== 'undefined'
/* eslint-disable max-len */
const IS_IOS = /iPad|iPhone|iPod/.test(agent) && !window.MSStream
const IS_ANDROID = /android/i.test(agent)
const IS_MOBILE_BROWSER =
  !!(agent && window.navigator && window.navigator.standalone) ||
  /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
    agent
  ) ||
  /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
    agent.substr(0, 4)
  )

const IS_12H_FORMAT = (() => {
  const date = new Date(Date.UTC(2019, 4, 4, 4, 4, 4))
  const dateString = date.toLocaleTimeString()
  return dateString.match(/am|pm/i) || date.toString().match(/am|pm/i)
})()

/* eslint-enable max-len */
export const SCREEN_SIZES = {
  zr: 0,
  xs: 599,
  sm: 959,
  md: 1280,
  lg: 1919,
  inf: 999999,
}

// Browser feature check methods
export const isMobileBrowser = () => IS_MOBILE_BROWSER
export const isDesktopBrowser = () => IS_BROWSER && !IS_MOBILE_BROWSER
export const isIOS = () => IS_IOS
export const isAndroid = () => IS_ANDROID
export const is12hFormat = () => IS_12H_FORMAT

/**
 * Utility function to solve styletron issue with browser
 * specific peuso elements. Right now it throws an error
 * if you will try to use like `::moz-placeholder` in styles.
 * This function check that the given pseudoelem name is valid,
 * and if it is valid it returns an object with given pseudoelem
 * with value. Otherwise returns empty object.
 *
 * @example
 * export const Input = styled('input', () => {
 *   const phStyles = {
 *     color: 'red'
 *   };
 *   return {
 *     ...safePseudoelem('::placeholder', phStyles),
 *     ...safePseudoelem('::-webkit-input-placeholder', phStyles),
 *     ...safePseudoelem('::-moz-placeholder', phStyles),
 *     ...safePseudoelem(':-ms-input-placeholder', phStyles)
 *   };
 * });
 * @param  {string} name  Pseudoelem name
 * @param  {object} value Pseudoelement value
 * @return {object}       Object `{ [name]: value }` if the element is valid
 *                        otherwise empty object.
 */
export const safePseudoelem = (name, value) => {
  if (IS_BROWSER && !(name in PSEUDOELEM_CHECK_CACHE)) {
    const style = document.createElement('style')
    document.head.appendChild(style)
    const { sheet } = style
    try {
      sheet.insertRule(`${name}{}`, 0)
      PSEUDOELEM_CHECK_CACHE[name] = true
    } catch (e) {
      PSEUDOELEM_CHECK_CACHE[name] = false
    }
    document.head.removeChild(style)
  }

  if (!IS_BROWSER || PSEUDOELEM_CHECK_CACHE[name]) {
    return { [name]: value }
  }
  return {}
}

/**
 * Returns an object with styles for some specific screen
 * size. Returns undefined if screen size is not defined
 * @param  {string} screenSize
 * @param  {object} styles
 * @return {object}
 */
export const getScreenStyles = (screenSize, screenStyles, otherwiseStyles) => {
  if (!screenSize || screenSize === 'auto') return otherwiseStyles
  const size = SCREEN_SIZES[screenSize] || SCREEN_SIZES.sm
  const result = {}
  if (screenStyles) {
    result[`@media (max-width: ${size}px)`] = screenStyles
  }
  if (otherwiseStyles) {
    result[`@media (min-width: ${size + 1}px)`] = otherwiseStyles
  }
  return result
}

/**
 * Return second argument if the device is mobile, first argument otherwise
 * @param  {Object} desktopStyles
 * @param  {OBject} mobileStyles
 * @return {Object}
 */
export const getDeviceStyles = (desktopStyles, mobileStyles) =>
  isMobileBrowser() ? mobileStyles : desktopStyles

// FIXME: refactor to always use debounce from utils
export { default as debounce } from 'mz-utils/debounce'

/**
 * Returns true if given value is nil - equal to undefined, null or empty string
 * @param  {any} val
 * @return {bool}
 */
export const isNil = (val, extraNill) =>
  val === null || val === undefined || val === '' || val === extraNill

/**
 * Check if given object is React's synthetic event or browser event
 * @param  {any} event
 * @return {bool}
 */
export const isSyntheticEvent = (event) =>
  !!(event && event.stopPropagation && event.preventDefault)

/**
 * Returns key name by it's code from event
 * @param  {any} event
 * @return {string}
 */
export const eventToKey = (event) => {
  const keyCode = event.keyCode || event.which || event.charCode

  const keyMap = {
    38: 'ArrowUp',
    40: 'ArrowDown',
    32: 'Space',
    27: 'Esc',
    13: 'Enter',
    9: 'Tab',
    8: 'Backspace',
  }

  return keyCode ? keyMap[keyCode] : event.key
}

/**
 * Fires event on document
 * @param {string} eventName
 * @param {object} data
 */
export const simulateDocumentEvent = (eventName, data) => {
  const event = new Event(eventName)
  Object.keys(data).forEach((key) => {
    event[key] = data[key]
  })
  document.dispatchEvent(event)
}

/**
 * Helper function to extend styles in order to
 * customize the appearance of an element
 * @param  {object} APPEARANCES       Each key of the object point to an appearance
 * @param  {string} defaultAppearance Key of default appearance
 */
export const getAppearanceFunc = (APPEARANCES, defaultAppearance) => (name) => (
  props
) => {
  const styles = APPEARANCES[props.$appearance || defaultAppearance]
  const styleGenerator =
    styles[name] || APPEARANCES[defaultAppearance][name] || {}
  return typeof styleGenerator === 'function'
    ? styleGenerator(props)
    : styleGenerator
}
